import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealText } from "@/components/RevealText";
import { ArrowRight } from "lucide-react";
import { HERO_IMAGE, HERO_IMAGE_MOBILE } from "@/data/content";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgYMobile = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] sm:min-h-[100vh] bg-bone overflow-hidden flex items-end pb-24 sm:pb-20 lg:pb-24 pt-28 sm:pt-32"
    >
      <div className="absolute inset-0">
        <motion.img
          style={{ y: imgYMobile, scale: imgScale }}
          src={HERO_IMAGE_MOBILE}
          alt="Swastik Healthcare reception"
          className="sm:hidden w-full h-[108%] object-cover object-bottom"
        />
        <motion.img
          style={{ y: imgY, scale: imgScale }}
          src={HERO_IMAGE}
          alt="Swastik Healthcare clinic"
          className="hidden sm:block w-full h-[120%] object-cover object-center"
        />

        {/* Mobile — localised scrim rising from the floor, leaves signage untouched */}
        <div
          className="absolute inset-0 sm:hidden"
          style={{
            background:
              "linear-gradient(to top, rgba(6,14,28,0.93) 0%, rgba(6,14,28,0.80) 26%, rgba(6,14,28,0.42) 44%, rgba(6,14,28,0.08) 58%, rgba(6,14,28,0) 68%)",
          }}
        />

        {/* Desktop overlays (unchanged) */}
        <div className="absolute inset-0 hidden sm:block bg-gradient-to-t from-black/75 via-black/30 to-black/5" />
        <div className="absolute inset-0 hidden sm:block bg-gradient-to-r from-black/55 via-black/15 to-transparent" />
        <div
          className="absolute inset-0 hidden sm:block"
          style={{
            background:
              "radial-gradient(ellipse 900px 650px at 18% 78%, rgba(0,0,0,0.65), transparent 65%)",
          }}
        />
        <div className="absolute inset-0 hero-grain pointer-events-none" />
      </div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="max-w-[1440px] mx-auto px-6 lg:px-12 w-full relative z-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <span
            className="hidden sm:flex text-teal text-[9px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] font-semibold whitespace-nowrap items-center gap-3 mb-4 sm:mb-6"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.95), 0 2px 14px rgba(0,0,0,0.9)" }}
            data-testid="hero-eyebrow"
          >
            <span className="hidden sm:inline-block h-px w-8 bg-teal/70" />
            Dehradun's Diagnostics &amp; Care Network
          </span>

          <h1
            className="font-display font-medium text-[2.15rem] sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-white"
            style={{ textShadow: "0 2px 6px rgba(0,0,0,0.9), 0 8px 30px rgba(0,0,0,0.6)" }}
            data-testid="hero-headline"
          >
            <RevealText lines={["Your Health,"]} delayStart={0.5} />
            <RevealText
              lines={["Decoded With"]}
              delayStart={0.65}
              className="italic text-teal"
            />
            <RevealText lines={["Precision & Care."]} delayStart={0.8} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="text-white/95 text-[13px] sm:text-base leading-relaxed max-w-md mt-5 sm:mt-7"
            style={{ textShadow: "0 2px 8px rgba(0,0,0,0.85)" }}
            data-testid="hero-subtext"
          >
            NABL-grade diagnostics, trusted specialists and doorstep sample
            collection — under one roof, so you never run between labs again.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.7 }}
            className="flex flex-wrap items-center gap-5 sm:gap-6 mt-7 sm:mt-9"
          >
            <a
              href="#book"
              data-testid="hero-book-cta"
              className="btn-sheen group inline-flex items-center gap-3 bg-teal text-white px-7 py-4 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-medium hover:bg-white hover:text-navy transition-colors duration-300"
            >
              Book a Test
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>
            <a
              href="#packages"
              data-testid="hero-explore-link"
              className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-white border-b border-white/40 pb-1 hover:border-teal hover:text-teal transition-colors duration-300"
            >
              Explore Packages
            </a>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="hidden sm:flex absolute bottom-10 right-8 lg:right-12 z-10 flex-col items-center gap-3"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/70 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <span className="scroll-cue" />
      </motion.div>
    </section>
  );
};
